import { useEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../constants/common'
import { BUILDING_GROUP_ENTITY } from '../../../../constants/entity'
import {
  useIsBuildingGroupProfileNGAvailable,
  useLinkLikeClick,
  usePageBrowserTabTitle,
  usePageShowReload,
  useSelection,
} from '../../../../hooks'
import { getBuildingGroupProfilePathname } from '../../../../utils/link'
import {
  useAsyncTable,
  useTableRefresh,
  useTableReloadAfterMassAction,
} from '../../../Table'

export function useBuildingGroupTablePage({
  asyncTableOptions,
  entity = BUILDING_GROUP_ENTITY,
  extraSelectionResetDeps = EMPTY_LIST,
  getPermanentFilters,
  isBrowserTabTitleEnabled = true,
  pageTitle,
  sub = null,
  onApplyTableConfig,
}) {
  const [isConfigOpen, setConfigOpen] = useState(false)

  const pageScrollWrapperRef = useRef(null)

  const asyncTable = useAsyncTable({
    ...asyncTableOptions,
    containerRef: pageScrollWrapperRef,
    entity,
    getPermanentFilters,
    sub,
  })

  const {
    columns,
    filterValues,
    handleReload,
    isInitiated,
    isRefetching,
    items,
    view,
  } = asyncTable

  usePageBrowserTabTitle({
    isEnabled: isBrowserTabTitleEnabled,
    isLoading: !isInitiated,
    pageTitle,
  })

  const selection = useSelection({
    data: items,
    pagination: asyncTable.pagination,
  })

  const { handleCleanSelection } = selection

  const {
    handleChangeShouldReloadFilters,
    handleReloadTableAfterMassAction,
    shouldReloadFilters,
  } = useTableReloadAfterMassAction({
    filterValues,
    getPermanentFilters,
    sub,
    onReload: handleReload,
  })

  const { refreshButtonProps } = useTableRefresh({
    isRefetching,
    onReload: handleReload,
  })

  usePageShowReload(handleReload)

  useEffect(() => {
    handleCleanSelection()
  }, [...extraSelectionResetDeps, filterValues])

  const { handleClick, handleMouseDown } = useLinkLikeClick()

  const isBuildingGroupProfileNGAvailable =
    useIsBuildingGroupProfileNGAvailable()

  function handleClickRow(id, event) {
    handleClick({
      event,
      link: getBuildingGroupProfilePathname({
        id,
        isBuildingGroupProfileNGAvailable,
      }),
    })
  }

  function handleMouseDownRow(id, event) {
    handleMouseDown({
      event,
      link: getBuildingGroupProfilePathname({
        id,
        isBuildingGroupProfileNGAvailable,
      }),
    })
  }

  async function handleApply(obj = EMPTY_OBJECT) {
    if (onApplyTableConfig) {
      return onApplyTableConfig({
        columns,
        view,
        ...obj,
      })
    }
  }

  function handleConfigOpen() {
    setConfigOpen(true)
  }

  return {
    ...asyncTable,
    ...selection,
    entity,
    handleApply,
    handleChangeShouldReloadFilters,
    handleClickRow,
    handleConfigOpen,
    handleMouseDownRow,
    handleReloadTableAfterMassAction,
    isConfigOpen,
    pageScrollWrapperRef,
    refreshButtonProps,
    setConfigOpen,
    shouldReloadFilters,
  }
}
